import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Keep react-router's location in sync with direct `window.location.hash` changes.
 *
 * Several modals are driven purely off `location.hash` via `useLocation()` (e.g. `AddCardModalWrapper`
 * switches the top-up / add-card success + failure modals on `#topUp_success` / `#addCard_success`).
 * That only works when react-router observes the hash change. But the checkout.com Flow path completes a
 * frictionless (non-3DS) payment by sending the browser to the session's `successUrl`, which is the SAME
 * path differing only by `#hash` — so the browser fires a `hashchange` WITHOUT reloading, and the
 * `history` package that backs react-router (which listens to `popstate`, not `hashchange`) never
 * notifies the router. The result: `useLocation()` is stale, so the success modal never shows and the
 * initiating slideout never dismisses. The 3DS path is unaffected because it returns cross-document from
 * checkout.com, forcing a full load that reads the hash fresh.
 *
 * Listening for `hashchange` here and replaying it into the router makes every direct hash change update
 * `useLocation()`. We use `replace` (not `push`) because the browser has already added the history entry
 * for the hash change; `replaceState` does not re-fire `hashchange`, so there is no loop.
 */
export function useSyncHashToRouter(): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onHashChange = (): void => {
      const { pathname, search, hash } = window.location;
      if (location.pathname !== pathname || location.search !== search || location.hash !== hash) {
        navigate({ pathname, search, hash }, { replace: true });
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [navigate, location.pathname, location.search, location.hash]);
}
